import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import _ from 'underscore';
import { useAngularService } from 'helpers/get_angular_service';
import useGym from 'helpers/use_gym';
import usePermissions from 'helpers/use_permissions';
import api from 'helpers/api';
import sources from 'helpers/sources';
import notification from 'helpers/notification';
import redirect from 'helpers/redirect';
import dateFormatter from 'helpers/date_formatter';
import currencyFormatter from 'helpers/currency_formatter';
import humanize from 'helpers/humanize';
import Button from 'react/components/button';
import Link from 'react/components/link';
import Icon from 'react/components/icon';
import type {
  MembershipWithCancellation,
  ContactWithMemberships,
  MembershipCancellation as MembershipCancellationType,
} from 'react/types/membership_cancellation';

const MembershipCancellation = () => {
  const $stateParams = useAngularService('$stateParams');
  const loadingAnimation = useAngularService('loadingAnimation');

  const gym = useGym();
  const permissions = usePermissions();

  const [contact, setContact] = useState<ContactWithMemberships | null>(null);
  const [membership, setMembership] = useState<MembershipWithCancellation | null>(null);
  const [cancellation, setCancellation] = useState<MembershipCancellationType | null>(null);

  const currency = gym?.currency_symbol || '$';

  const newExpiryDate = cancellation?.cancel_on
    ? moment(cancellation.cancel_on).subtract(1, 'days').format('YYYY-MM-DD')
    : null;

  const cancellationFeePresent = (): boolean => {
    return parseFloat(String(cancellation?.cancellation_fee || 0)) > 0;
  };

  const handleDelete = (): void => {
    if (!window.confirm('Are you sure you want to delete this cancellation?')) {
      return;
    }

    if (!gym?.id || !contact?.id || !cancellation?.id) return;

    loadingAnimation.show();
    const url = sources.membershipCancellation(
      gym.id,
      $stateParams.contactType,
      contact.id,
      cancellation.id,
    );

    api.delete(url).then(
      () => {
        notification.success('Membership cancellation deleted successfully');
        redirect('gym.contacts.edit', { contactType: 'members', contactId: contact.id });
      },
      () => {
        loadingAnimation.hide();
        notification.error('An error occurred deleting membership cancellation.');
      },
    );
  };

  useEffect(() => {
    if (!gym?.id || !$stateParams.contactId || !$stateParams.contactType) return;

    const url = sources.contactWithType({
      gymId: gym.id,
      contactType: $stateParams.contactType,
      contactId: $stateParams.contactId,
    });

    api.get(url).then((contactData: ContactWithMemberships) => {
      setContact(contactData);
      const membershipData = _.find(
        contactData.memberships,
        (m: MembershipWithCancellation) =>
          m.membership_cancellation?.id === parseInt($stateParams.cancellationId, 10),
      );
      setMembership(membershipData || null);

      if (membershipData?.membership_cancellation) {
        setCancellation(membershipData.membership_cancellation);
      }

      if (!membershipData?.id) {
        notification.error('Unable to find membership');
        redirect('gym.contacts.edit', { contactId: contactData.id });
      }
    });
  }, [gym?.id, $stateParams.contactId, $stateParams.contactType, $stateParams.cancellationId]);

  if (!contact || !membership || !cancellation) {
    return <div>Loading...</div>;
  }

  const canManageMemberships = permissions.manage_memberships && permissions.delete_memberships;

  return (
    <section className="content-section">
      <header className="page-header page-header--mobile-column">
        <h1 className="page-header__title">Membership Cancellation</h1>
        <div className="page-header__buttons">
          {cancellation.editable && canManageMemberships && (
            <Link
              type="button"
              variant="primary"
              route="gym.contacts.editMembershipCancellation"
              routeParams={{ contactId: contact.id, cancellationId: cancellation.id }}
            >
              Edit
            </Link>
          )}
          {cancellation.deletable && canManageMemberships && (
            <Button variant="hollow" onClick={handleDelete}>
              <Icon name="trash" width={20} height={20} />
            </Button>
          )}
          <Link
            type="button"
            variant="hollow"
            route="gym.contacts.edit"
            routeParams={{ contactType: 'members', contactId: contact.id }}
          >
            Back
          </Link>
        </div>
      </header>

      <div className="container">
        <main>
          <table className="table worx-table worx-table--no-border-top">
            <tbody>
              <tr>
                <td>Cancellation added</td>
                <td className="is-primary">
                  {cancellation.cancellation_added_at} by {cancellation.cancelled_by_name}
                </td>
              </tr>
              <tr>
                <td>Contact</td>
                <td className="is-primary">
                  <Link
                    route="gym.contacts.edit"
                    routeParams={{ contactType: 'members', contactId: contact.id }}
                  >
                    {contact.name}
                  </Link>
                </td>
              </tr>
              <tr>
                <td>Membership</td>
                <td className="is-primary">
                  <Link route="gym.memberships.show" routeParams={{ membershipId: membership.id }}>
                    {membership.label}
                  </Link>
                </td>
              </tr>
              <tr>
                <td>Previous expiry date</td>
                <td className="is-primary">
                  {cancellation.expiration_date_before_cancellation
                    ? dateFormatter(cancellation.expiration_date_before_cancellation)
                    : 'N/A'}
                </td>
              </tr>
              <tr>
                <td>Cancelled from</td>
                <td className="is-primary">{dateFormatter(cancellation.cancel_on)}</td>
              </tr>
              <tr>
                <td>New expiry date</td>
                <td className="is-primary">{newExpiryDate ? dateFormatter(newExpiryDate) : ''}</td>
              </tr>
              <tr>
                <td>Reason for cancellation</td>
                <td className="is-primary">{cancellation.reason || 'N/A'}</td>
              </tr>
              <tr>
                <td>Cancellation fee</td>
                <td className="is-primary">
                  {cancellationFeePresent() ? (
                    <span>
                      {currencyFormatter(cancellation.cancellation_fee, currency)} to be taken on{' '}
                      {dateFormatter(cancellation.cancellation_fee_due_on || '')}
                    </span>
                  ) : (
                    <span>N/A</span>
                  )}
                </td>
              </tr>
              {cancellation.cancellation_fee_status && (
                <tr>
                  <td>Cancellation fee status</td>
                  <td className="is-primary">{humanize(cancellation.cancellation_fee_status)}</td>
                </tr>
              )}
              <tr>
                <td>Payments</td>
                <td className="is-primary">
                  {cancellation.change_payments === 'delete_payments_after_cancellation' ? (
                    <span>Let payments continue until cancellation date</span>
                  ) : (
                    <span>Delete all payments between now and cancellation date</span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </main>
      </div>
    </section>
  );
};

export default MembershipCancellation;
